import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { FormApi } from 'final-form';
import React, { useState, useContext, useCallback } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Option, OptionUpdatePayload } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RestaurantApiError } from 'apis/restaurant-api/types';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { getModifiedFieldsForTracking, useEditOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import AddOptionValueButton from './OptionForm/AddOptionValueButton';
import OptionForm from './OptionForm/OptionForm';
import OptionFormFields from './OptionForm/OptionFormFields';
import OptionValueAdvancedDetailsModal from './OptionForm/OptionValueAdvancedDetailsModal';
import UnpurchasableItemWarning from './OptionForm/UnpurchasableItemWarning';
import simulateUpdateOptionAPICall from './simulateUpdateOptionAPICall';
import { MenuPermissionsContext } from '../../components/MenuPermissionsContext';
import { OptionFormData } from '../../types';
import { getItemsThatWillBecomeUnpurchasable } from '../../utils';
import { buildUpdateOptionFormData, buildOptionUpdatePayload, sanitizeOptionFormData } from '../utils';

type Props = {
  error: null | RestaurantApiError;
  language: Language;
  menuId: string;
  onCancel: () => void;
  onSubmit: (optionUpdatePayload: OptionUpdatePayload) => void;
  option: Option;
  submitting: boolean;
};

const Root = styled.form`
  display: flex;
  flex-direction: column;
`;

const OptionValueActions = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  white-space: nowrap;
  margin-block-end: 12px;
`;

const ProductInfoButton = styled(TextButton)``;

const FormError = styled(Alert).attrs({ variant: 'error' })`
  margin-block-end: 16px;
`;

const Buttons = styled.div`
  display: flex;
`;

const OptionEditor = ({ error, language, onCancel, onSubmit, option, submitting }: Props) => {
  const { items, options } = useMenuData();

  const { currency } = useContext(CurrencyContext);
  const menuPermissions = useContext(MenuPermissionsContext);
  const [isAdvancedDetailsModalOpen, setIsAdvancedDetailsModalOpen] = useState(false);
  const handleProductInfoModalClose = useCallback(() => setIsAdvancedDetailsModalOpen(false), []);
  const trackEditOption = useEditOptionsTracking();

  const handleSubmit = (formData: OptionFormData, form: FormApi<OptionFormData, Partial<OptionFormData>>) => {
    trackEditOption(option.id, getModifiedFieldsForTracking<OptionFormData>(form.getState()));
    onSubmit(buildOptionUpdatePayload(language, currency, option.menu_id, option, sanitizeOptionFormData(formData)));
  };

  const getNewUnpurchasableItems = (values: OptionFormData) => {
    const optionUpdatePayload = buildOptionUpdatePayload(
      language,
      currency,
      option.menu_id,
      option,
      sanitizeOptionFormData(values),
    );
    const simulatedUpdatedOption = simulateUpdateOptionAPICall(optionUpdatePayload, option);
    return getItemsThatWillBecomeUnpurchasable(simulatedUpdatedOption, items, options);
  };

  return (
    <OptionForm initialValues={buildUpdateOptionFormData(language, option)} onSubmit={handleSubmit}>
      {({ handleSubmit, pristine, valid, values }) => {
        const itemsThatWillBecomeUnpurchasable = valid ? getNewUnpurchasableItems(values) : null;
        return (
          <>
            <Root onSubmit={handleSubmit}>
              <OptionFormFields allowChangingOptionType={false} />
              <UnpurchasableItemWarning
                isMultiChoice={option.type === 'multi_choice'}
                items={itemsThatWillBecomeUnpurchasable}
                language={language}
              />
              {menuPermissions.contentEdit && (
                <OptionValueActions>
                  <Field<OptionFormData['type']> name="type">
                    {({ input: { value } }) =>
                      (value === 'choice' || value === 'multi_choice') && <AddOptionValueButton />
                    }
                  </Field>
                  <ProductInfoButton
                    variant="blue"
                    onClick={() => {
                      setIsAdvancedDetailsModalOpen(true);
                    }}
                  >
                    <FormattedMessage id="menu-option.edit-advanced-details" />
                  </ProductInfoButton>
                </OptionValueActions>
              )}
              {error && (
                <FormError>
                  <FormattedMessage id={error && (error.apiUiMessage || 'errors.unknown-error-retry-contact')} />
                </FormError>
              )}
              <Buttons>
                <Button
                  type="submit"
                  disabled={submitting || pristine}
                  variant="blue"
                  size="small"
                  loading={submitting}
                >
                  <FormattedMessage id="menu.saveChanges" />
                </Button>
                <Button style={{ marginInlineStart: 16 }} variant="lightBlue" size="small" onClick={onCancel}>
                  <FormattedMessage id="state.cancel" />
                </Button>
              </Buttons>
            </Root>
            <OptionValueAdvancedDetailsModal
              isOpen={isAdvancedDetailsModalOpen}
              onClose={handleProductInfoModalClose}
            />
            <MenuEditorConfirmationPrompt when={!pristine} />
          </>
        );
      }}
    </OptionForm>
  );
};

export default OptionEditor;
